import { Source, Layer } from 'react-map-gl';
import { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { type Point, featureCollection, point } from '@turf/helpers';
import { COLOR_TEXT_PRIMARY_INVERSE, FONT_SIZE_100, FONT_SIZE_50 } from '@alltrails/denali/tokens';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { useAppSelector, useAppDispatch } from '../../redux';
import useSvgImages from '../../utils/useSvgImages';
import { defaultSymbolLayerLayoutProps, userCustomMapPinClusterId, userCustomMapPinId, defaultPinLayerLayoutProps } from '../../utils/layers';
import createTrailPinSvg from '../../utils/svgDefinitions/createTrailPinSvg';
import useMap from '../../hooks/useMap';
import userCustomMapPinClusterSvg from '../../utils/svgDefinitions/userCustomMapPinClusterSvg';
import userCustomMapPinClusterHoveredSvg from '../../utils/svgDefinitions/userCustomMapPinClusterHoveredSvg';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import { setHoveredUserCustomRouteClusterId, setActiveRouteIds } from '../../redux/reducer';
import { UserCustomRoute } from '../../types/CustomRoute';
import useGroupHoverMapBoxEvents from '../../hooks/useGroupHoverMapBoxEvents';
import ActiveRoutePolylines from './ActiveRoutePolylines';
import ActiveUserCustomRoutePopup from './ActiveUserCustomRoutePopup';

const sourceId = 'user-custom-maps-source';
const userCustomMapPinImageName = 'user-custom-map-pin';
const userCustomMapPinActiveImageName = 'user-custom-map-pin-active';
const userCustomMapClusterHoveredPin = 'user-custom-map-pin-cluster-trailpin-hovered';
const userCustomMapClusterPin = 'user-custom-map-pin-cluster-trailpin';
const minPopupZoom = 11;
const textOffset: [number, number] = [0, -0.2];

type UserCustomRoutesProps = {
  onPopupClick: (id: number, slug: string) => void;
};

const UserCustomRoutes = ({ onPopupClick }: UserCustomRoutesProps) => {
  const dispatch = useAppDispatch();
  const { userCustomRoutes, displayedUserCustomRouteIds, allowClickEvents, hoveredUserCustomRouteClusterId, activeRouteIds } = useAppSelector(
    state => state.map
  );
  const map = useMap();
  const [activePolylineIds, setActivePolylineIds] = useState<number[]>([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupCoords, setPopupCoords] = useState<{ lat: number; lng: number } | null>(null);
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const { registerBaseMapEventHandlers, unregisterBaseMapEventHandlers } = useContext(MapLayerInteractionsContext);
  const { isFavorite, handleFavoriteClick, listModal } = useReduxListItems();
  // Show popup and polyline for single pin on hover, close everything on empty hover
  const onHover = useCallback(
    (event: any) => {
      if (event?.features && event.features?.length > 0) {
        const feature = event.features[0];
        if (!feature.properties.cluster) {
          dispatch(setActiveRouteIds([feature.properties.id]));
          dispatch(setHoveredUserCustomRouteClusterId(undefined));
          setActivePolylineIds([feature.properties.id]);
          setCarouselIndex(0);
          setPopupCoords({
            lat: event.lngLat.lat,
            lng: event.lngLat.lng
          });
        }
      }
    },
    [dispatch]
  );

  // Reset everything on mouse leave for pins
  const onHoverEnd = useCallback(() => {
    // Only close popup if not hovering the popup
    if (!hoveredUserCustomRouteClusterId) {
      dispatch(setActiveRouteIds(undefined));
      setActivePolylineIds([]);
    }
  }, [dispatch, hoveredUserCustomRouteClusterId]);

  const { onMouseEnter, onMouseLeave } = useGroupHoverMapBoxEvents(onHover, onHoverEnd, true);

  // Memoized list of routes to display
  const routesToDisplay = useMemo(
    () => Object.values(userCustomRoutes).filter(route => displayedUserCustomRouteIds.includes(route.id)),
    [userCustomRoutes, displayedUserCustomRouteIds]
  );

  // SVG definitions for pins and clusters
  const svgDefinitions = useMemo(
    () => [
      createTrailPinSvg({ imageName: userCustomMapPinImageName, trailCount: 1, isCustomRoute: true }),
      createTrailPinSvg({ imageName: userCustomMapPinActiveImageName, trailCount: 1, isActive: true, isCustomRoute: true }),
      createTrailPinSvg({ imageName: userCustomMapClusterHoveredPin, trailCount: 2, isActive: true, isCustomRoute: true }),
      createTrailPinSvg({ imageName: userCustomMapClusterPin, trailCount: 2, isCustomRoute: true }),
      userCustomMapPinClusterSvg,
      userCustomMapPinClusterHoveredSvg
    ],
    []
  );

  useEffect(() => {
    if (activeRouteIds && activeRouteIds.length > 0) {
      setPopupOpen(true);
    } else {
      setPopupOpen(false);
    }
  }, [activeRouteIds]);

  const handleMapClick = useCallback(
    (e: any) => {
      if (!e.features || !e.features.some((f: any) => f.layer.id === userCustomMapPinId || f.layer.id === userCustomMapPinClusterId)) {
        dispatch(setActiveRouteIds([]));
        setActivePolylineIds([]);
      }
    },
    [dispatch]
  );

  const getClusterMapIds = useCallback(
    (mapbox: mapboxgl.Map | undefined, clusterId: number, callback: (mapIds: number[], features: any[], coordinates: number[]) => void) => {
      if (!mapbox) return;
      const source = mapbox.getSource(sourceId);
      if (source) {
        (source as mapboxgl.GeoJSONSource).getClusterLeaves(clusterId, 100, 0, (err: any, features?: any[] | null) => {
          if (!err && features) {
            const mapIds = features.map(f => f.properties?.id);
            // Pass coordinates if needed
            const coordinates = features[0]?.geometry?.coordinates ?? [];
            callback(mapIds, features, coordinates);
          }
        });
      }
    },
    []
  );

  // Show cluster polylines on hover, close popup if open for a single pin
  const onClusterMouseMove = useCallback(
    (event: any) => {
      if (!map || !event.features || event.features.length === 0) return;

      const clusterFeature = event.features[0];
      const clusterId = clusterFeature?.properties?.cluster_id;
      if (!clusterId) return;

      if (clusterId !== hoveredUserCustomRouteClusterId) {
        dispatch(setActiveRouteIds([]));
      }

      dispatch(setHoveredUserCustomRouteClusterId(clusterId));

      const zoom = map.getZoom();
      if (zoom >= minPopupZoom) {
        getClusterMapIds(map.getMap() as any, clusterId, mapIds => {
          setActivePolylineIds(mapIds);
        });
      }
    },
    [map, dispatch, getClusterMapIds, hoveredUserCustomRouteClusterId]
  );

  // Only clear polylines on cluster mouse leave (never close popup here)
  const onClusterMouseLeave = useCallback(() => {
    if (!popupOpen) {
      setActivePolylineIds([]);
    }
  }, [popupOpen]);

  // Only zoom on cluster click if below minPopupZoom
  const onClusterClick = useCallback(
    (event: any) => {
      if (!map || !event.features || event.features.length === 0) {
        return;
      }
      const clusterFeature = event.features[0];
      const clusterId = clusterFeature?.properties?.cluster_id;
      if (!clusterId) {
        return;
      }
      const zoom = map.getZoom();

      if (zoom < minPopupZoom) {
        const source = map.getSource(sourceId);
        if (source) {
          (source as mapboxgl.GeoJSONSource).getClusterExpansionZoom(clusterId, (err: any, expansionZoom?: number | null) => {
            if (!err && expansionZoom) {
              map.easeTo({
                center: clusterFeature.geometry.coordinates,
                zoom: expansionZoom
              });
            }
          });
        }
      } else {
        getClusterMapIds(map as any, clusterId, (mapIds, _features, _coordinates) => {
          dispatch(setActiveRouteIds(mapIds));
          setActivePolylineIds(mapIds);
          setCarouselIndex(0);
          setPopupCoords({
            lat: clusterFeature.geometry.coordinates[1],
            lng: clusterFeature.geometry.coordinates[0]
          });
        });
      }
    },
    [map, dispatch, getClusterMapIds]
  );
  // Open popup on pin or cluster click (if zoomed in enough)
  const onPinClick = useCallback(
    (event: any) => {
      if (!map) return;
      const zoom = map.getZoom();
      if (zoom < minPopupZoom) return;

      if (event.features && event.features.length > 0) {
        const feature = event.features[0];
        if (feature.properties.cluster) {
          const clusterId = feature.properties.cluster_id;
          const source = map.getSource(sourceId);
          if (source) {
            (source as mapboxgl.GeoJSONSource).getClusterLeaves(clusterId, 100, 0, (err: any, features?: any[] | null) => {
              if (!err && features) {
                const mapIds = features.map(f => f.properties?.id);
                setActiveRouteIds(mapIds);
                setActivePolylineIds(mapIds);
                setCarouselIndex(0);
                setPopupCoords({
                  lat: feature.geometry.coordinates[1],
                  lng: feature.geometry.coordinates[0]
                });
              }
            });
          }
        } else {
          setActiveRouteIds([feature.properties.id]);
          setActivePolylineIds([feature.properties.id]);
          setCarouselIndex(0);
          setPopupCoords({
            lat: feature.geometry.coordinates[1],
            lng: feature.geometry.coordinates[0]
          });
        }
      }
    },
    [map]
  );

  useEffect(() => {
    if (!allowClickEvents) {
      return;
    }
    registerEventHandler(userCustomMapPinClusterId, 'mousemove', onClusterMouseMove);
    registerEventHandler(userCustomMapPinClusterId, 'mouseleave', onClusterMouseLeave);
    registerEventHandler(userCustomMapPinClusterId, 'click', onClusterClick);

    onMouseEnter && registerEventHandler(userCustomMapPinId, 'mousemove', onMouseEnter);
    onMouseLeave && registerEventHandler(userCustomMapPinId, 'mouseleave', onMouseLeave);
    registerEventHandler(userCustomMapPinId, 'click', onPinClick);

    registerBaseMapEventHandlers({
      click: { handler: handleMapClick }
    });

    return () => {
      unregisterEventHandler(userCustomMapPinClusterId, 'mousemove', onClusterMouseMove);
      unregisterEventHandler(userCustomMapPinClusterId, 'mouseleave', onClusterMouseLeave);
      unregisterEventHandler(userCustomMapPinClusterId, 'click', onClusterClick);

      onMouseEnter && unregisterEventHandler(userCustomMapPinId, 'mousemove', onMouseEnter);
      onMouseLeave && unregisterEventHandler(userCustomMapPinId, 'mouseleave', onMouseLeave);
      unregisterEventHandler(userCustomMapPinId, 'click', onPinClick);
      unregisterBaseMapEventHandlers(['click']);
    };
  }, [
    map,
    onClusterMouseMove,
    onClusterMouseLeave,
    onClusterClick,
    onPinClick,
    onMouseEnter,
    onMouseLeave,
    registerEventHandler,
    unregisterEventHandler,
    unregisterBaseMapEventHandlers,
    registerBaseMapEventHandlers,
    allowClickEvents,
    handleMapClick
  ]);

  const handleCarouselChange = useCallback((route: any, index: number) => {
    setCarouselIndex(index);
    setActivePolylineIds([route.id]);
  }, []);

  const onFavoriteClick = useCallback(
    (currentResult: UserCustomRoute) => {
      const isCustomRoute = !currentResult.missing_polyline;
      handleFavoriteClick(currentResult.type, currentResult.id, currentResult.contentPrivacy, isCustomRoute);
    },
    [handleFavoriteClick]
  );

  // Memoized popup rendering
  const popup = useMemo(() => {
    if (!popupOpen || !activeRouteIds || !activeRouteIds.length) return null;
    const routes = activeRouteIds.map(id => userCustomRoutes[id]).filter(Boolean);
    if (!routes.length) return null;
    const { lat, lng } = routes.length == 1 ? routes[0]._geoloc : popupCoords || routes[0]._geoloc;
    return (
      <ActiveUserCustomRoutePopup
        latitude={lat}
        longitude={lng}
        routes={routes}
        initialRouteIndex={carouselIndex}
        onRouteChange={handleCarouselChange}
        onClick={onPopupClick}
        onEnter={onMouseEnter}
        onLeave={onMouseLeave}
        isFavorite={isFavorite}
        onFavoriteClick={onFavoriteClick}
      />
    );
  }, [
    popupOpen,
    activeRouteIds,
    userCustomRoutes,
    carouselIndex,
    handleCarouselChange,
    popupCoords,
    onMouseEnter,
    onMouseLeave,
    isFavorite,
    onFavoriteClick,
    onPopupClick
  ]);

  const isHoveredExpression = ['==', ['get', 'id'], activeRouteIds?.[0] ?? -1];
  const imagesAreLoaded = useSvgImages(svgDefinitions);

  const data = useMemo(
    () => featureCollection<Point>(routesToDisplay.map(({ id, _geoloc }) => point([_geoloc.lng, _geoloc.lat], { id }))),
    [routesToDisplay]
  );

  if (!userCustomRoutes || data.features.length === 0 || !imagesAreLoaded) {
    return null;
  }

  const zoom = map?.getZoom() ?? 0;

  return (
    <>
      <Source id={sourceId} type="geojson" data={data} cluster={true}>
        {/* Cluster layer for low zoom */}
        {zoom < minPopupZoom ? (
          <Layer
            id={userCustomMapPinClusterId}
            type="symbol"
            source={sourceId}
            filter={['has', 'point_count']}
            layout={{
              ...defaultSymbolLayerLayoutProps,
              'text-size': ['step', ['get', 'point_count'], FONT_SIZE_100, 100, FONT_SIZE_50, 1000, 8],
              'text-anchor': 'center',
              'text-field': '{point_count_abbreviated}',
              'text-offset': [0, 0],
              'icon-image': [
                'case',
                ['==', ['get', 'cluster_id'], hoveredUserCustomRouteClusterId ?? null],
                userCustomMapPinClusterHoveredSvg.name,
                userCustomMapPinClusterSvg.name
              ]
            }}
            paint={{ 'text-color': COLOR_TEXT_PRIMARY_INVERSE }}
          />
        ) : (
          <Layer
            id={userCustomMapPinClusterId}
            type="symbol"
            source={sourceId}
            filter={['has', 'point_count']}
            layout={{
              ...defaultSymbolLayerLayoutProps,
              'text-size': ['step', ['get', 'point_count'], FONT_SIZE_100, 100, FONT_SIZE_50, 1000, 8],
              'text-anchor': 'center',
              'text-field': '{point_count_abbreviated}',
              'text-offset': textOffset,
              'icon-image': [
                'case',
                ['==', ['get', 'cluster_id'], hoveredUserCustomRouteClusterId ?? null],
                userCustomMapClusterHoveredPin,
                userCustomMapClusterPin
              ]
            }}
            paint={{ 'text-color': COLOR_TEXT_PRIMARY_INVERSE }}
          />
        )}
        {/* Layer for single pins */}
        <Layer
          id={userCustomMapPinId}
          type="symbol"
          source={sourceId}
          filter={['!', ['has', 'point_count']]}
          layout={{
            ...defaultSymbolLayerLayoutProps,
            ...defaultPinLayerLayoutProps,
            'icon-image': ['case', isHoveredExpression, userCustomMapPinActiveImageName, userCustomMapPinImageName]
          }}
        />
      </Source>
      <ActiveRoutePolylines mapIds={activePolylineIds} />
      {popup}
      {listModal}
    </>
  );
};

export default UserCustomRoutes;
